import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, KeySquare, RefreshCw } from 'lucide-react';
import { McpKeyIdentityContext } from '@sleed/bizcommerce-api';
import type { McpKeyToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Pill } from '~/components/pill';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { formatDateTime } from '~/lib/format-date';
import { useMcpKeysList, useMcpKeyMutations } from '~/hooks/use-mcp-keys';
import { useUsersList } from '~/hooks/use-users';
import { tokenToForm, type McpKeyFormValues } from '~/lib/schemas/mcp-key';
import { McpKeyMintDialog } from './mcp-key-mint-dialog';
import { MintedTokenDialog } from './minted-token-dialog';

/**
 * MCP keys surface — mint (one-time token, optionally creating the backing service user
 * inline), edit grants, regenerate, revoke, delete. The UI twin of mint-mcp-key.ps1
 * over McpKeysController (docs/ai/02b).
 */
export function McpKeysGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useMcpKeysList();
	const { data: users = [] } = useUsersList();
	const { regenerate, revoke, remove } = useMcpKeyMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<McpKeyFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<McpKeyToken | null>(null);
	const [revokeRow, setRevokeRow] = useState<McpKeyToken | null>(null);
	const [minted, setMinted] = useState<{ token: string; name: string } | null>(null);

	const backingLabel = useMemo(
		() => (k: McpKeyToken) =>
			k.context === McpKeyIdentityContext.Admin
				? (users.find(u => u.id === k.backingUserId)?.eml ?? (k.backingUserId ? `user #${k.backingUserId}` : '—'))
				: k.backingCustomerId
					? `customer #${k.backingCustomerId}`
					: '—',
		[users]
	);

	const columns = useMemo<SystemGridColumn<McpKeyToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.mcpKeys.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: k => k.name ?? '',
				exportValue: k => k.name ?? '',
				render: k => (
					<div className="min-w-0">
						<div className="truncate text-sm font-medium text-foreground">{k.name}</div>
						<div className="truncate text-[11px] text-foreground-muted">{backingLabel(k)}</div>
					</div>
				)
			},
			{
				key: 'key',
				header: t('system.mcpKeys.columns.key'),
				width: '110px',
				exportValue: k => `••••${k.keyLast4 ?? ''}`,
				render: k => <span className="font-mono text-xs text-foreground-muted">••••{k.keyLast4 || '????'}</span>
			},
			{
				key: 'context',
				header: t('system.mcpKeys.columns.context'),
				width: '120px',
				align: 'center',
				sortValue: k => k.context ?? 0,
				exportValue: k =>
					k.context === McpKeyIdentityContext.Admin ? t('system.mcpKeys.contextAdmin') : t('system.mcpKeys.contextFrontEnd'),
				render: k =>
					k.context === McpKeyIdentityContext.Admin ? (
						<Pill variant="info">{t('system.mcpKeys.contextAdmin')}</Pill>
					) : (
						<Pill variant="neutral">{t('system.mcpKeys.contextFrontEnd')}</Pill>
					)
			},
			{
				key: 'tools',
				header: t('system.mcpKeys.columns.tools'),
				width: '110px',
				align: 'center',
				sortValue: k => k.tools?.length ?? 0,
				exportValue: k => (k.tools ?? []).map(g => g.toolName).join(', '),
				render: k => (
					<span
						className="rounded-pill bg-muted px-2 py-0.5 text-xs font-medium text-foreground-muted"
						title={(k.tools ?? []).map(g => `${g.toolName}${g.requiresApproval ? ' ✓' : ''}`).join('\n')}>
						{t('system.mcpKeys.toolCount', { count: k.tools?.length ?? 0 })}
					</span>
				)
			},
			{
				key: 'lastUsed',
				header: t('system.mcpKeys.columns.lastUsed'),
				width: '170px',
				sortValue: k => (k.lastUsedAt ? String(k.lastUsedAt) : ''),
				exportValue: k => formatDateTime(k.lastUsedAt),
				render: k => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(k.lastUsedAt)}</span>
			},
			{
				key: 'status',
				header: t('system.mcpKeys.columns.status'),
				width: '110px',
				align: 'center',
				sortValue: k => (k.revokedAt ? 1 : 0),
				exportValue: k => (k.revokedAt ? t('system.mcpKeys.revoked') : t('system.mcpKeys.active')),
				render: k =>
					k.revokedAt ? (
						<Pill variant="danger">{t('system.mcpKeys.revoked')}</Pill>
					) : (
						<Pill variant="success">{t('system.mcpKeys.active')}</Pill>
					)
			},
			{
				key: 'createdAt',
				header: t('system.fields.createdAt'),
				width: '170px',
				defaultVisible: false,
				sortValue: k => (k.createdAt ? String(k.createdAt) : ''),
				exportValue: k => formatDateTime(k.createdAt),
				render: k => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(k.createdAt)}</span>
			},
			{
				key: 'createdBy',
				header: t('system.fields.createdBy'),
				width: '200px',
				defaultVisible: false,
				sortValue: k => k.createdByEmail ?? '',
				exportValue: k => k.createdByEmail ?? '',
				render: k => <span className="truncate text-sm text-foreground-muted">{k.createdByEmail || '—'}</span>
			},
			{
				key: 'revokedAt',
				header: t('system.fields.revokedAt'),
				width: '170px',
				defaultVisible: false,
				sortValue: k => (k.revokedAt ? String(k.revokedAt) : ''),
				exportValue: k => formatDateTime(k.revokedAt),
				render: k => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(k.revokedAt)}</span>
			}
		],
		[t, backingLabel]
	);

	const onRegenerate = (k: McpKeyToken) => {
		if (!k.id) return;
		regenerate.mutate(k.id, {
			onSuccess: res => {
				if (res.token) setMinted({ token: res.token, name: k.name ?? '' });
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	const confirmRevoke = () => {
		if (!revokeRow?.id) return;
		revoke.mutate(revokeRow.id, {
			onSuccess: () => {
				toast.success(t('system.mcpKeys.revokedToast'));
				setRevokeRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<McpKeyToken>
				gridId="mcp-keys"
				title={t('system.mcpKeys.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={k => k.id ?? k.name ?? ''}
				columns={columns}
				searchPredicate={(k, q) =>
					`${k.name ?? ''} ${k.createdByEmail ?? ''} ${(k.tools ?? []).map(g => g.toolName).join(' ')}`.toLowerCase().includes(q)
				}
				defaultSort={{ key: 'createdAt', dir: 'desc' }}
				emptyIcon={<KeySquare size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.mcpKeys.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={k => {
					if (k.revokedAt) return;
					setEditing(tokenToForm(k));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={k =>
					k.revokedAt ? null : (
						<>
							<Button
								variant="ghost"
								size="icon"
								type="button"
								aria-label={t('system.mcpKeys.regenerate')}
								title={t('system.mcpKeys.regenerate')}
								disabled={regenerate.isPending}
								onClick={() => onRegenerate(k)}>
								<RefreshCw size={15} />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								type="button"
								aria-label={t('system.mcpKeys.revoke')}
								title={t('system.mcpKeys.revoke')}
								onClick={() => setRevokeRow(k)}>
								<Ban size={15} className="text-danger-fg" />
							</Button>
						</>
					)
				}
				actionsWidth={148}
				exportFilename="mcp-keys"
			/>

			<McpKeyMintDialog
				open={editOpen}
				onOpenChange={setEditOpen}
				initial={editing}
				onMinted={(token, name) => setMinted({ token, name })}
			/>

			<MintedTokenDialog token={minted?.token ?? null} keyName={minted?.name} onClose={() => setMinted(null)} />

			<Dialog open={Boolean(revokeRow)} onOpenChange={open => !open && setRevokeRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.mcpKeys.revokeTitle')}</DialogTitle>
						<DialogDescription>{t('system.mcpKeys.revokeBody', { name: revokeRow?.name ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setRevokeRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmRevoke} disabled={revoke.isPending}>
							{t('system.mcpKeys.revoke')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.mcpKeys.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.mcpKeys.deleteBody', { name: deleteRow?.name ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
